import {
  CONFIGURABLE_INTEGRATIONS,
  INTEGRATION_ENUM,
} from "../../constants/constants";
import { validateTwilioCredentials } from "../../features/twilio/twilioAPI";

export const encodeState = (payload = {}) => {
  return btoa(JSON.stringify({ ...payload, timestamp: Date.now() }))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
};

export const decodeState = (state) => {
  try {
    const base64 = state.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch (err) {
    console.error("Invalid state");
    return null;
  }
};

export const createAuthState = (user, integration, isOnboarding) => {
  let payload = {
    userId: user.uuid,
    integrationId: integration.uuid,
    device: "web",
    accountId: user.org,
    isOnboarding,
    service: integration.name,
  };

  return encodeState(payload);
};

export const getIntegrationAuthUrl = (
  integrationId,
  integrationName,
  state,
) => {
  const name = integrationName?.toLowerCase();

  switch (name) {
    case INTEGRATION_ENUM.FATHOM:
      return `${process.env.REACT_APP_SERVER}/fathom/auth/${state}`;

    case INTEGRATION_ENUM.SLACK:
      return `${process.env.REACT_APP_SERVER}/slack/auth/${state}`;

    case INTEGRATION_ENUM.DISCORD:
    case INTEGRATION_ENUM.GMAIL:
      return `${process.env.REACT_APP_SERVER}/general-auth/auth/${integrationId}?state=${encodeURIComponent(state)}`;

    case INTEGRATION_ENUM.READ_AI:
      return `${process.env.REACT_APP_SERVER}/read-ai/auth/${state}`;

    case INTEGRATION_ENUM.WHATSAPP:
      return `${process.env.REACT_APP_SERVER}/whatsapp/auth/${state}`;

    case INTEGRATION_ENUM.OUTLOOK:
    case INTEGRATION_ENUM.TEAMS:
      return `${process.env.REACT_APP_SERVER}/microsoft/auth/${state}`;

    case INTEGRATION_ENUM.INSTAGRAM_MESSAGES:
    case INTEGRATION_ENUM.INSTAGRAM_COMMENTS:
    case INTEGRATION_ENUM.FACEBOOK_READONLY_MESSAGES:
    case INTEGRATION_ENUM.INSTAGRAM_READONLY_MESSAGES:
    case INTEGRATION_ENUM.FACEBOOK_MESSAGES:
    case INTEGRATION_ENUM.FACEBOOK_COMMENTS:
    case INTEGRATION_ENUM.INTERCOM:
      return `${process.env.REACT_APP_SERVER}/general-auth/auth/${integrationId}?state=${encodeURIComponent(state)}`;

    default:
      return `${process.env.REACT_APP_SERVER}/integration/auth/${state}`;
  }
};

export const getApiKeySteps = {
  granola: [
    "Login to your Granola account",
    "Go to Settings → API Keys",
    "Generate a new API key",
    "Copy the API key securely",
  ],
  crisp: [
    "Log into your Crisp account at app.crisp.chat",
    "Select the website/workspace you want the token for",
    "Go to Settings > Website Settings > Advanced configuration",
    "Find the API Token section and click Generate Token",
    "Copy the token_id, token_key and website_id immediately — the key is shown only once",
  ],
  twilio: [
    "Log in to your Twilio Console",
    "On the dashboard, find the Account Info section",
    "Copy your Account SID",
    "Click the eye icon to reveal your Auth Token",
    "Copy your Account SID and Auth Token securely",
  ],
};

export const requireSetup = (integrationName, setting) => {
  if (!integrationName) return false;

  if (setting?.isInstallationCompleted === false) return false;

  const requiresSetup = CONFIGURABLE_INTEGRATIONS.some((integration) =>
    integrationName.toLowerCase().includes(integration),
  );

  if (!requiresSetup) return true;

  const isMeta = [
    INTEGRATION_ENUM.FACEBOOK_MESSAGES,
    INTEGRATION_ENUM.FACEBOOK_COMMENTS,
    INTEGRATION_ENUM.FACEBOOK_READONLY_MESSAGES,
    INTEGRATION_ENUM.INSTAGRAM_MESSAGES,
    INTEGRATION_ENUM.INSTAGRAM_COMMENTS,
    INTEGRATION_ENUM.INSTAGRAM_READONLY_MESSAGES,
  ].some((name) => integrationName.toLowerCase() === name?.toLowerCase());

  if (isMeta) {
    return Boolean(
      setting?.isInstallationCompleted && setting?.connectionIds?.id1,
    );
  }

  const defaultSettings = setting?.defaultSettings;

  if (!defaultSettings || defaultSettings.length === 0) return false;

  return defaultSettings.every((defaultSetting) => {
    if (!defaultSetting?.name) return false;
    if (!defaultSetting?.data || defaultSetting.data.length === 0) return false;
    return defaultSetting.data.every((item) => item?.key && item?.value);
  });
};

export const getIntegrationConfig = (platform) => {
  switch (platform) {
    case INTEGRATION_ENUM.TEAMS:
      return {
        itemLabel: "Select Joined Team",
        title: "Select Joined Team & Channel",
        workspaceLabel: "Select Team",
        connectMessage:
          "Teams is not connected for this setting. Please connect Teams and try again.",
        showWorkspace: true,
      };
    case INTEGRATION_ENUM.TWILIO:
      return {
        itemLabel: "Select Number",
        title: "Select a Twilio Number",
        workspaceLabel: "Select Number",
        connectMessage:
          "Twilio is not connected for this setting. Please connect Twilio and try again.",
        showWorkspace: true,
      };
    case INTEGRATION_ENUM.FACEBOOK_MESSAGES:
    case INTEGRATION_ENUM.FACEBOOK_COMMENTS:
    case INTEGRATION_ENUM.FACEBOOK_READONLY_MESSAGES:
      return {
        itemLabel: "Select Page",
        title: "Select Facebook Page",
        workspaceLabel: "Select Page",
        connectMessage:
          "Facebook is not connected for this setting. Please connect Facebook and try again.",
        showWorkspace: true,
      };
    case INTEGRATION_ENUM.INSTAGRAM_MESSAGES:
    case INTEGRATION_ENUM.INSTAGRAM_COMMENTS:
    case INTEGRATION_ENUM.INSTAGRAM_READONLY_MESSAGES:
      return {
        itemLabel: "Select Page",
        title: "Select Instagram Page",
        workspaceLabel: "Select Page",
        connectMessage:
          "Instagram is not connected for this setting. Please connect Instagram and try again.",
        showWorkspace: true,
      };
    default:
      return {
        itemLabel: "Select Project",
        title: "Select Default Settings",
        workspaceLabel: "Select Workspace",
        connectMessage: "Integration is not connected for this setting.",
        showWorkspace: true,
      };
  }
};

export const INTEGRATION_OPTIONS = {
  [INTEGRATION_ENUM.SLACK?.toLowerCase()]: {
    settingsLoad: "onLoad",
    preSteps: [],
    title: "Select Default Settings",
  },
  [INTEGRATION_ENUM.GMAIL?.toLowerCase()]: {
    settingsLoad: "onLoad",
    preSteps: [],
    title: "Select Default Settings",
  },
  [INTEGRATION_ENUM.OUTLOOK?.toLowerCase()]: {
    settingsLoad: "onLoad",
    preSteps: [],
    title: "Select Default Settings",
  },
  [INTEGRATION_ENUM.DISCORD?.toLowerCase()]: {
    settingsLoad: "onLoad",
    preSteps: [],
    title: "Select Default Settings",
  },
  [INTEGRATION_ENUM.WHATSAPP?.toLowerCase()]: {
    settingsLoad: "onLoad",
    preSteps: [],
    title: "Select Default Settings",
  },
  [INTEGRATION_ENUM.FACEBOOK_MESSAGES?.toLowerCase()]: {
    settingsLoad: "onLoad",
    preSteps: [{ key: "page", label: "Select Page", dataKey: "pages" }],
    hasDefaultSettings: false,
    title: "Select Facebook Page",
  },
  [INTEGRATION_ENUM.FACEBOOK_COMMENTS?.toLowerCase()]: {
    settingsLoad: "onLoad",
    preSteps: [{ key: "page", label: "Select Page", dataKey: "pages" }],
    hasDefaultSettings: false,
    title: "Select Facebook Page",
  },
  [INTEGRATION_ENUM.FACEBOOK_READONLY_MESSAGES?.toLowerCase()]: {
    settingsLoad: "onLoad",
    preSteps: [{ key: "page", label: "Select Page", dataKey: "pages" }],
    hasDefaultSettings: false,
    title: "Select Facebook Page",
  },
  [INTEGRATION_ENUM.INSTAGRAM_MESSAGES?.toLowerCase()]: {
    settingsLoad: "onLoad",
    preSteps: [{ key: "page", label: "Select Page", dataKey: "pages" }],
    hasDefaultSettings: false,
    title: "Select Instagram Page",
  },
  [INTEGRATION_ENUM.INSTAGRAM_COMMENTS?.toLowerCase()]: {
    settingsLoad: "onLoad",
    preSteps: [{ key: "page", label: "Select Page", dataKey: "pages" }],
    hasDefaultSettings: false,
    title: "Select Instagram Page",
  },
  [INTEGRATION_ENUM.INSTAGRAM_READONLY_MESSAGES?.toLowerCase()]: {
    settingsLoad: "onLoad",
    preSteps: [{ key: "page", label: "Select Page", dataKey: "pages" }],
    hasDefaultSettings: false,
    title: "Select Instagram Page",
  },
  [INTEGRATION_ENUM.TWILIO.toLowerCase()]: {
    settingsLoad: "onLoad",
    preSteps: [],
    title: "Select Number for sync",
  },
  [INTEGRATION_ENUM.TEAMS?.toLowerCase()]: {
    settingsLoad: "onItemSelect",
    preSteps: [{ key: "team", label: "Select Team", dataKey: "workspaces" }],
    title: "Select Joined Team and Channel",
  },
};

export const DEFAULT_SETUP_CONFIG = {
  title: "Enter API Key",
  description:
    "Enter your API key or access token to import all your meetings from your AI meeting platform and automatically extract tasks, summaries, and insights.",
  fields: [
    {
      name: "apiKey",
      label: "API key",
      placeholder: "Enter API Key or Access Token",
      type: "password",
      iconKey: "key",
    },
  ],
  buildPayload: (values, integration) => ({
    integrationId: integration?.uuid,
    accessToken: values.apiKey,
    isInstallationCompleted: true,
  }),
};

export const SETUP_CONFIG = {
  [INTEGRATION_ENUM.CRISP]: {
    title: "Enter Token ID, Token Key and Website ID",
    description:
      "Enter your Token ID, Token Key and Website ID to import all your conversations from Crisp and automatically extract tasks, summaries, and insights.",
    fields: [
      {
        name: "tokenId",
        label: "Token ID",
        placeholder: "Enter Token ID",
        type: "text",
        iconKey: "key",
      },
      {
        name: "tokenKey",
        label: "Token Key",
        placeholder: "Enter Token Key",
        type: "password",
        iconKey: "key",
      },
      {
        name: "websiteId",
        label: "Website ID",
        placeholder: "Enter Website ID",
        type: "text",
        iconClass: "webconnect-globe",
      },
    ],
    redirectToConfiguration: false,
    buildPayload: (values, integration) => ({
      integrationId: integration?.uuid,
      accessToken: values.tokenKey,
      connectionIds: { id1: values.tokenId, id2: values.websiteId },
      isInstallationCompleted: true,
    }),
  },

  [INTEGRATION_ENUM.TWILIO]: {
    title: "Enter Account SID and Auth Token",
    description:
      "Enter your Twilio Account SID and Auth Token to import messages from your Twilio account and automatically extract tasks, summaries, and insights.",
    fields: [
      {
        name: "accountSID",
        label: "Account SID",
        placeholder: "Enter Account SID",
        type: "text",
        iconClass: "webconnect-file-person",
        autoComplete: "off",
      },
      {
        name: "authToken",
        label: "Auth Token",
        placeholder: "Enter Auth Token",
        type: "password",
        iconKey: "key",
        autoComplete: "off",
      },
    ],
    redirectToConfiguration: true,
    beforeSubmit: (values) =>
      validateTwilioCredentials({
        accountSid: values.accountSID,
        authToken: values.authToken,
      }),
    buildPayload: (values, integration) => ({
      integrationId: integration?.uuid,
      accessToken: values.authToken,
      connectionIds: { id1: values.accountSID },
      isInstallationCompleted: true,
    }),
  },

  [INTEGRATION_ENUM.FELLOW_AI]: {
    ...DEFAULT_SETUP_CONFIG,
    title: "Enter API Key and Sub Domain",
    description:
      "Enter your API key or access token and Sub Domain to import all your meetings from your AI meeting platform and automatically extract tasks, summaries, and insights.",
    fields: [
      ...DEFAULT_SETUP_CONFIG.fields,
      {
        name: "subDomain",
        label: "Subdomain",
        placeholder: "Enter sub domain",
        type: "text",
        iconClass: "webconnect-globe",
        suffix: ".fellow.app",
      },
    ],
    buildPayload: (values, integration) => ({
      integrationId: integration?.uuid,
      accessToken: values.apiKey,
      subDomain: values.subDomain,
      isInstallationCompleted: true,
    }),
  },
};

export const getSetupConfig = (integrationName) =>
  SETUP_CONFIG[integrationName] || DEFAULT_SETUP_CONFIG;
